import {Component, OnDestroy, OnInit} from '@angular/core';
import {RulesService} from "../../services/rules.service";
import {Contact} from "../../model/contact";
import {Group} from "../../model/group";
import {ModalsService} from "../../services/modals.service";
import {Subscription} from "rxjs";
import {SubscriberService} from "../../services/subscriber.service";
import {Router} from "@angular/router";

@Component({
  selector: 'app-subscriber-rules-page',
  templateUrl: './subscriber-rules-page.component.html',
  styleUrls: ['./subscriber-rules-page.component.css'],
  host: {
    class: 'app-page'
  }
})
export class SubscriberRulesPageComponent implements OnInit, OnDestroy {
  contacts: Array<Contact>;
  groups: Array<Group>;

  activeTab: Tab;

  privetActive: boolean;

  subscription: Subscription;

  constructor(private readonly rulesService: RulesService,
              private readonly subscriberService: SubscriberService,
              private readonly modalsService: ModalsService,
              private readonly router: Router) {
    this.contacts = new Array<Contact>();
    this.groups = new Array<Group>();

    this.activeTab = Tab.CONTACTS;

    this.privetActive = false;

    this.subscription = new Subscription();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.subscriberService.subscriber
        .subscribe(subscriber => {
          if (subscriber) {
            this.privetActive = subscriber.activeSubscription;
            if (!this.privetActive) {
              this.router.navigate(['/error-404']);
            }
          } else {
            this.privetActive = false;
          }
        })
    );

    this.subscription.add(
      this.rulesService.contacts
        .subscribe(contacts => {
          this.contacts = this.filterContacts(contacts);
        })
    );

    this.subscription.add(
      this.rulesService.groups
        .subscribe(groups => {
          this.groups = groups.map(group => {
            const filteredNumbers = this.filterGroupNumbers(group.numbers);

            const filteredGroup = new Group(
              group.id,
              group.title,
              filteredNumbers
            );
            filteredGroup.rules = group.rules;
            return filteredGroup;
          });
        })
    );

    this.rulesService.loadRules();
  }

  filterContacts(contacts: Array<Contact>): Array<Contact> {
    if (!contacts || contacts.length === 0) {
      return [];
    }

    const safeContacts = contacts.map(contact => ({
      ...contact,
      rules: Array.isArray(contact.rules) ? contact.rules : []
    }));

    const groupsByMsisdn = new Map<string, Contact[]>();

    safeContacts.forEach(contact => {
      const key = contact.msisdn;
      if (!groupsByMsisdn.has(key)) {
        groupsByMsisdn.set(key, []);
      }
      groupsByMsisdn.get(key).push(contact);
    });

    const result: Contact[] = [];

    for (const [msisdn, group] of groupsByMsisdn) {
      if (group.length === 1) {
        result.push(group[0]);
      } else {
        const filtered = group.filter(contact => contact.title !== msisdn);
        result.push(...filtered);
      }
    }

    return this.sortContacts(result);
  }

  filterGroupNumbers(numbers: Array<Contact>): Array<Contact> {
    if (!numbers || numbers.length === 0) {
      return [];
    }

    const bestContacts: { [msisdn: string]: Contact } = {};

    for (let i = 0; i < numbers.length; i++) {
      const contact = numbers[i];
      const msisdn = contact.msisdn;
      const safeRules = Array.isArray(contact.rules) ? contact.rules : [];

      if (!bestContacts[msisdn]) {
        bestContacts[msisdn] = { ...contact, rules: [...safeRules] };
      } else {
        const existing = bestContacts[msisdn];
        const existingIsGood = existing.title !== existing.msisdn;
        const newIsGood = contact.title !== contact.msisdn;
        const existingRules = Array.isArray(existing.rules) ? existing.rules : [];
        const mergedRules = [...existingRules, ...safeRules];

        if (newIsGood && !existingIsGood) {
          bestContacts[msisdn] = { ...contact, rules: mergedRules };
        } else {
          bestContacts[msisdn] = { ...existing, rules: mergedRules };
        }
      }
    }

    let result = Object.values(bestContacts);

    return this.sortContacts(result);
  }

  private sortContacts(contacts: Array<Contact>): Array<Contact> {
    if (!contacts || contacts.length === 0) {
      return [];
    }

    return contacts.sort((a, b) => {
      const aIsGood = a.title !== a.msisdn;
      const bIsGood = b.title !== b.msisdn;

      if (aIsGood !== bIsGood) {
        return aIsGood ? -1 : 1;
      }

      const nameA = aIsGood ? a.title : a.msisdn;
      const nameB = bIsGood ? b.title : b.msisdn;
      return nameA.localeCompare(nameB);
    });
  }

  showContacts(): void {
    this.activeTab = Tab.CONTACTS;
  }

  showGroups(): void {
    this.activeTab = Tab.GROUPS;
  }

  addContact(): void {
    if (this.privetActive) {
      this.modalsService.showNewContact();
    }
  }

  addGroup(): void {
    if (this.privetActive && this.contacts?.length > 0) {
      this.modalsService.showNewGroup();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected readonly Tab = Tab;
}
enum Tab {
  CONTACTS,
  GROUPS
}
